import { debounce } from "@/utils/debounce"
import { Sentry } from "@/telemetry/sentry"

const measurements = new Map<string, number[]>()

const isEnabled = () => {
	return true
	// return !import.meta.env.PROD
}

const addMeasurement = (name: string, start: DOMHighResTimeStamp, end: DOMHighResTimeStamp) => {
	if (import.meta.env.PROD) {
		// Disable for production
		return
	}

	if (!measurements.has(name)) {
		measurements.set(name, [])
	}

	const measurement = measurements.get(name) as number[]
	measurement.push(end - start)

	// Set mark in browser timeline
	performance.measure(name, { start, end })

	logMeasurementsDebounced()
}

const noop = Function.prototype

export function measured(name?: string) {
	return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
		if (!isEnabled()) {
			return
		}

		const method = descriptor.value
		const timingName = `${target.constructor.name}: ${name ?? propertyKey}`

		descriptor.value = function (...args: any[]) {
			const endMeasurement = startMeasurement(timingName)
			// eslint-disable-next-line @typescript-eslint/no-unsafe-call
			const result = method.apply(this, args)

			if (result instanceof Promise) {
				result.then(endMeasurement as AnyFunction).catch((error) => {
					Sentry.captureException(error)
					throw error
				})
			} else {
				endMeasurement()
			}

			// eslint-disable-next-line @typescript-eslint/no-unsafe-return
			return result
		}
	}
}

export const startMeasurement = (name: string) => {
	if (!isEnabled()) {
		return noop
	}

	const span = Sentry.startInactiveSpan({ name })
	const start = performance.now()

	return () => {
		// End measurement
		const end = performance.now()
		span?.end()
		addMeasurement(name, start, end)
	}
}

export const logMeasurements = () => {
	if (!isEnabled()) {
		return
	}

	if (measurements.size) {
		console.group(`Performance`)
	}

	for (const [name, timings] of measurements.entries()) {
		const min = Math.min(...timings)
		const max = Math.max(...timings)
		const avg =
			timings.reduce((total, val) => {
				return total + val
			}, 0) / timings.length

		console.log(
			`${name} (${timings.length} samples)
  | min: ${min.toFixed(2)}ms
  | max: ${max.toFixed(2)}ms
  | avg: ${avg.toFixed(2)}ms`,
		)
	}

	if (measurements.size) {
		console.groupEnd()
	}
}

const logMeasurementsDebounced = debounce(logMeasurements, 1000)

Promise.prototype.measured = function (name: string) {
	if (!isEnabled()) {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		return this
	}

	const endMeasurement = startMeasurement(name)

	// eslint-disable-next-line @typescript-eslint/no-unsafe-return
	return this.then((result) => {
		endMeasurement()
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		return result
	})
}

Function.prototype.measured = function (name?: string) {
	if (!isEnabled()) {
		return this
	}

	const func = this

	return function () {
		const endMeasurement = startMeasurement(name ?? func.name)
		// @ts-expect-error `this` implicitly has type `any`
		const result = func.apply(this, arguments)
		endMeasurement()
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		return result
	}
}

if (import.meta.env.DEV) {
	globalThis.calculateMeasurements = logMeasurements
}
